import React, { useState } from "react";
import { Dialog, DialogContent, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { formatNumber } from "../../../utils/formatNumber";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const POBreakdownGrid = ({
  open,
  onClose,
  poData,
  transaction,
  allPoItems,
  poUsageJson,
}) => {
  const [expandedGroups, setExpandedGroups] = useState({});
  const isPayed = transaction?.status === "payed";
  const isCanceled = transaction?.status === "cancel";

  const toggleGroup = (poNumber) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [poNumber]: !prev[poNumber],
    }));
  };

  const summaryMap = new Map();
  const noSellMap = new Map();
  const savedUsage = Array.isArray(poUsageJson) ? poUsageJson : [];
  const hasSnapshot =
    savedUsage.length > 0 &&
    savedUsage.some(
      (item) =>
        item &&
        typeof item === "object" &&
        (item.actualWeightBefore !== undefined ||
          item.actualWeightAfter !== undefined ||
          item.poWeight !== undefined ||
          item.productName !== undefined),
    );

  if (hasSnapshot) {
    const byPoNumber = new Map();

    for (const item of savedUsage) {
      if (!item || typeof item !== "object") continue;

      const poNumber =
        String(item.poNumber || item.poId || "")
          .split("-")
          .pop()
          ?.padStart(3, "0") ?? "-";
      const usedWeight = Number(item.usedWeight ?? 0) || 0;
      const poWeight = Number(item.poWeight ?? 0) || 0;
      const actualWeightBefore = Number(item.actualWeightBefore ?? 0) || 0;
      const actualWeightAfter =
        item.actualWeightAfter != null
          ? Number(item.actualWeightAfter) || 0
          : actualWeightBefore + usedWeight;
      const unlimited = !!item.unlimitedWeight;

      if (!byPoNumber.has(poNumber)) {
        byPoNumber.set(poNumber, {
          poNumber,
          totalCurrentWeight: 0,
          totalNetWeight: 0,
          totalPoWeight: 0,
          isUnlimited: false,
          items: [],
        });
      }

      const group = byPoNumber.get(poNumber);
      const row = {
        id: `${item.poItemId ?? item.productId ?? poNumber}-${usedWeight > 0 ? "used" : "unused"}`,
        subCategory: item.subCategory || "อื่นๆ",
        productTitle: item.productName || "",
        unit: "กก.",
        totalPoUsedWeight: usedWeight,
        totalNetWeight: actualWeightAfter,
        totalPoWeight: poWeight,
        unlimited,
        currentWeight: usedWeight,
        poWeightText: `${formatNumber(actualWeightBefore)} / ${unlimited ? "ไม่จำกัด" : formatNumber(poWeight)}`,
        overWeight: unlimited ? 0 : actualWeightAfter - poWeight,
      };

      group.items.push(row);
      group.totalCurrentWeight += usedWeight;
      group.totalNetWeight += actualWeightAfter;
      group.totalPoWeight += poWeight;
      if (unlimited) group.isUnlimited = true;
    }

    for (const group of byPoNumber.values()) {
      for (const item of group.items) {
        if (item.totalPoUsedWeight > 0) {
          if (!summaryMap.has(group.poNumber)) {
            summaryMap.set(group.poNumber, {
              poNumber: group.poNumber,
              totalCurrentWeight: 0,
              totalNetWeight: 0,
              totalPoWeight: 0,
              isUnlimited: false,
              items: [],
            });
          }
          const soldGroup = summaryMap.get(group.poNumber);
          soldGroup.items.push(item);
          soldGroup.totalCurrentWeight += item.totalPoUsedWeight;
          soldGroup.totalNetWeight += item.totalNetWeight;
          soldGroup.totalPoWeight += item.totalPoWeight;
          if (item.unlimited) soldGroup.isUnlimited = true;
        } else {
          if (!noSellMap.has(group.poNumber)) {
            noSellMap.set(group.poNumber, {
              poNumber: group.poNumber,
              totalNetWeight: 0,
              totalPoWeight: 0,
              isUnlimited: false,
              items: [],
            });
          }
          const unsoldGroup = noSellMap.get(group.poNumber);
          unsoldGroup.items.push({
            ...item,
            currentWeight: 0,
            totalPoUsedWeight: 0,
          });
          unsoldGroup.totalNetWeight += item.totalNetWeight - item.totalPoUsedWeight;
          unsoldGroup.totalPoWeight += item.totalPoWeight;
          if (item.unlimited) unsoldGroup.isUnlimited = true;
        }
      }
    }
  } else {
    // 1. Calculate actual usage in this transaction
    const usageMap = new Map();
    console.log("DEBUG poData:", poData);
    if (Array.isArray(poData)) {
      poData.forEach((item) => {
        if (!Array.isArray(item.details)) return;

        const netWeight = item.originalItem?.netWeight || 0;
        const poUsages = new Map();

        item.details.forEach((detail) => {
          if (detail.priceSource !== "po") return;

          const po = detail.poInfo;
          const productId = po?.product?.id || detail.productId || item.productId;
          const purchaseOrderId =
            String(po?.poNumber || po?.purchaseOrderId || "")
              .split("-")
              .pop()
              ?.padStart(3, "0") ?? "-";
          const poItemIdKey = `${purchaseOrderId}-${productId}`;

          if (!poUsages.has(poItemIdKey)) {
            poUsages.set(poItemIdKey, { poInfo: po, usedWeight: 0 });
          }
          poUsages.get(poItemIdKey).usedWeight += detail.usedWeight || 0;
        });

        for (const [poItemIdKey, poData] of poUsages.entries()) {
          if (!usageMap.has(poItemIdKey)) {
            usageMap.set(poItemIdKey, { used: 0, net: 0, poInfo: poData.poInfo });
          }
          const usage = usageMap.get(poItemIdKey);
          usage.used += poData.usedWeight;
          usage.net += netWeight;
        }
      });
    }

    // 2. Iterate all items to place them in either Sold or Unsold
    if (Array.isArray(allPoItems)) {
      allPoItems.forEach((poGroup) => {
        const items = Array.isArray(poGroup.items) ? poGroup.items : [poGroup];

        items.forEach((item) => {
          if (!item || !item.productId) return;

          const purchaseOrderId =
            String(poGroup.poNumber || "")
              .split("-")
              .pop()
              ?.padStart(3, "0") ?? "-";
          const productId = item.productId;
          const poItemIdKey = `${purchaseOrderId}-${productId}`;

          const poWeight = item.poWeight || 0;
          const historicalUsed = item.actualWeight || 0;
          const unlimited = item.unlimitedWeight;
          const productTitle = item.product?.title || item.productName || "";
          const subCategory = item.product?.subCategory?.name || "";

          const usage = usageMap.get(poItemIdKey);
          const usedWeight = usage ? usage.used : 0;
          const txNetWeight = usage ? usage.net : 0;
          const totalNetWeight = historicalUsed + txNetWeight;

          if (usage) {
            if (!summaryMap.has(purchaseOrderId)) {
              summaryMap.set(purchaseOrderId, {
                poNumber: purchaseOrderId,
                totalCurrentWeight: 0,
                totalNetWeight: 0,
                totalPoWeight: 0,
                isUnlimited: false,
                items: [],
              });
            }
            const group = summaryMap.get(purchaseOrderId);
            if (!group.items.find((i) => i.id === poItemIdKey)) {
              group.items.push({
                id: poItemIdKey,
                subCategory,
                productTitle,
                unit: "กก.",
                totalPoUsedWeight: usedWeight,
                totalNetWeight: totalNetWeight,
                totalPoWeight: poWeight,
                unlimited,
              });
              group.totalCurrentWeight += usedWeight;
              group.totalNetWeight += totalNetWeight;
              group.totalPoWeight += poWeight;
              if (unlimited) group.isUnlimited = true;
            }
          } else {
            if (!noSellMap.has(purchaseOrderId)) {
              noSellMap.set(purchaseOrderId, {
                poNumber: purchaseOrderId,
                items: [],
              });
            }
            if (
              !noSellMap
                .get(purchaseOrderId)
                .items.find((i) => i.id === `${poItemIdKey}-unused`)
            ) {
              noSellMap.get(purchaseOrderId).items.push({
                id: `${poItemIdKey}-unused`,
                subCategory,
                productTitle,
                unit: "กก.",
                currentWeight: 0,
                poWeightText: `${formatNumber(totalNetWeight)} / ${unlimited ? "ไม่จำกัด" : formatNumber(poWeight)}`,
                overWeight: unlimited ? 0 : totalNetWeight - poWeight,
                unlimited,
              });
            }
          }
        });
      });
    } else {
      for (const [poItemIdKey, usage] of usageMap.entries()) {
        const { poInfo } = usage;
        const purchaseOrderId =
          String(poInfo?.poNumber || poInfo?.purchaseOrderId || "")
            .split("-")
            .pop()
            ?.padStart(3, "0") ?? "-";
        const poNumber = purchaseOrderId;
        const subCategory = poInfo?.product?.subCategory?.name || "";
        const productTitle = poInfo?.product?.title || "";
        const poWeight = poInfo?.poWeight || 0;
        const historicalUsed = poInfo?.actualWeight || 0;
        const unlimited = poInfo?.unlimitedWeight;
        const totalNetWeight = historicalUsed + usage.net;

        if (usage) {
          if (!summaryMap.has(purchaseOrderId)) {
            summaryMap.set(purchaseOrderId, {
              poNumber: poNumber,
              totalCurrentWeight: 0,
              totalNetWeight: 0,
              totalPoWeight: 0,
              isUnlimited: false,
              items: [],
            });
          }
          const group = summaryMap.get(purchaseOrderId);
          group.totalCurrentWeight += usage.used;
          group.totalNetWeight += totalNetWeight;
          group.totalPoWeight += poWeight;
          if (unlimited) group.isUnlimited = true;

          group.items.push({
            id: poItemIdKey,
            subCategory,
            productTitle,
            unit: "กก.",
            totalPoUsedWeight: usage.used,
            totalNetWeight: totalNetWeight,
            totalPoWeight: poWeight,
            unlimited,
          });
        } else {
          if (!noSellMap.has(purchaseOrderId)) {
            noSellMap.set(purchaseOrderId, {
              poNumber: purchaseOrderId,
              totalNetWeight: 0,
              totalPoWeight: 0,
              isUnlimited: false,
              items: [],
            });
          }
          const group = noSellMap.get(purchaseOrderId);
          group.totalNetWeight += totalNetWeight;
          group.totalPoWeight += poWeight;
          if (unlimited) group.isUnlimited = true;

          group.items.push({
            id: `${poItemIdKey}-unused`,
            subCategory,
            productTitle,
            unit: "กก.",
            currentWeight: 0,
            poWeightText: `${formatNumber(totalNetWeight)} / ${unlimited ? "ไม่จำกัด" : formatNumber(poWeight)}`,
            overWeight: unlimited ? 0 : totalNetWeight - poWeight,
            unlimited,
          });
        }
      }
    }
  }

  const soldGroups = Array.from(summaryMap.values()).sort((a, b) => b.poNumber.localeCompare(a.poNumber));
  const noSellGroups = Array.from(noSellMap.values()).sort((a, b) => b.poNumber.localeCompare(a.poNumber));

  const renderOverWeight = (val, isUnlimited) => {
    if (isUnlimited || val === 0) return <span className="text-slate-500">-</span>;
    if (val < 0) return <span className="text-red-500 font-bold">{formatNumber(val)}</span>;
    return <span className="text-amber-500 font-bold">+{formatNumber(val)}</span>;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth PaperProps={{ style: { borderRadius: '16px', maxHeight: '85vh', height: '85vh' } }}>
      <div className="flex justify-between items-center p-4 border-b border-gray-100 bg-gray-50/50">
        <h2 className="text-xl font-bold text-slate-800">สรุปการซื้อ PO</h2>
        <IconButton onClick={onClose} size="small" className="text-slate-400 hover:text-slate-600 hover:bg-slate-100">
          <CloseIcon />
        </IconButton>
      </div>

      <DialogContent className="!p-0 bg-gray-50/30 flex flex-col h-full overflow-hidden">
        <div className="w-full flex-1 overflow-auto">
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-gray-500 uppercase bg-gray-100/50 sticky top-0">
              <tr>
                <th className="px-4 py-3 w-8"></th>
                <th className="px-4 py-3 min-w-[120px]">PO #</th>
                <th className="px-4 py-3 min-w-[150px]">หมวดหมู่ย่อย</th>
                <th className="px-4 py-3 min-w-[150px]">สินค้า</th>
                <th className="px-4 py-3 text-center w-24">หน่วย</th>
                <th className="px-4 py-3 text-right min-w-[150px]">น้ำหนักซื้อจากตัวนี้</th>
                <th className="px-4 py-3 text-right min-w-[200px]">น้ำหนักซื้อจริงทั้งหมด / <span className="text-gray-400">น้ำหนัก PO</span></th>
                <th className="px-6 py-3 text-right min-w-[150px]">
                  <span className="text-red-500">น้ำหนักขาด</span> / <span className="text-amber-500">เกิน</span>
                </th>
              </tr>
            </thead>

            <tbody>
              {soldGroups.map((group) => {
                const isExpanded = !!expandedGroups[group.poNumber];
                return (
                  <React.Fragment key={group.poNumber}>
                    {/* Group Header Row */}
                    <tr
                      className="border-b border-gray-100 bg-white hover:bg-gray-50/50 transition-colors cursor-pointer select-none"
                      onClick={() => toggleGroup(group.poNumber)}
                    >
                      <td className="px-4 py-3 text-gray-400 text-center">
                        {isExpanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                      </td>
                      <td className="px-4 py-3 font-bold text-slate-700" colSpan={4}>
                        {group.poNumber}
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-slate-700">
                        {formatNumber(group.totalCurrentWeight)}
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-slate-700">
                        <span className="text-slate-800">{formatNumber(group.totalNetWeight)}</span>
                        <span className="text-gray-400 font-normal"> / {group.isUnlimited ? "ไม่จำกัด" : formatNumber(group.totalPoWeight)}</span>
                      </td>
                      <td className="px-6 py-3 text-right">
                        {renderOverWeight(group.isUnlimited ? 0 : group.totalNetWeight - group.totalPoWeight, group.isUnlimited)}
                      </td>
                    </tr>

                    {/* Group Item Rows via SubCategory */}
                    {isExpanded && (() => {
                      const subCatMap = {};
                      group.items.forEach(item => {
                        const maxC = item.subCategory || "อื่นๆ";
                        if (!subCatMap[maxC]) {
                          subCatMap[maxC] = {
                            name: maxC,
                            items: [],
                            totalPoUsedWeight: 0,
                            totalNetWeight: 0,
                            totalPoWeight: 0,
                            unlimited: false
                          };
                        }
                        subCatMap[maxC].items.push(item);
                        subCatMap[maxC].totalPoUsedWeight += (item.totalPoUsedWeight || 0);
                        subCatMap[maxC].totalNetWeight += (item.totalNetWeight || 0);
                        subCatMap[maxC].totalPoWeight += (item.totalPoWeight || 0);
                        if (item.unlimited) subCatMap[maxC].unlimited = true;
                      });

                      return Object.values(subCatMap).map(subCat => {
                        const subCatKey = `${group.poNumber}-${subCat.name}`;
                        const isSubExpanded = !!expandedGroups[subCatKey];
                        const overWeightSub = subCat.unlimited ? 0 : (subCat.totalNetWeight - subCat.totalPoWeight);

                        return (
                          <React.Fragment key={subCatKey}>
                            <tr
                              className="border-b border-gray-50 bg-gray-50/70 hover:bg-gray-100/70 transition-colors cursor-pointer select-none"
                              onClick={() => toggleGroup(subCatKey)}
                            >
                              <td className="px-4 py-2"></td>
                              <td className="px-4 py-2 text-center text-gray-400">
                                {isSubExpanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                              </td>
                              <td className="px-4 py-2 font-bold text-slate-700" colSpan={3}>
                                {subCat.name}
                              </td>
                              <td className="px-4 py-2 text-right font-bold text-slate-600">
                                {formatNumber(subCat.totalPoUsedWeight)}
                              </td>
                              <td className="px-4 py-2 text-right">
                                <span className="font-bold text-slate-800">{formatNumber(subCat.totalNetWeight)}</span>
                                <span className="text-gray-400 ml-1">
                                  / {subCat.unlimited ? "ไม่จำกัด" : formatNumber(subCat.totalPoWeight)}
                                </span>
                              </td>
                              <td className="px-6 py-2 text-right">
                                {renderOverWeight(overWeightSub, subCat.unlimited)}
                              </td>
                            </tr>

                            {/* Items inside SubCategory */}
                            {isSubExpanded && subCat.items.map((item) => {
                              const overWeight = item.unlimited ? 0 : (item.totalNetWeight - item.totalPoWeight);
                              return (
                                <tr key={item.id} className="border-b border-gray-50 bg-white hover:bg-gray-50 transition-colors">
                                  <td className="px-4 py-2"></td>
                                  <td className="px-4 py-2"></td>
                                  <td className="px-4 py-2 text-center text-gray-300">
                                    <KeyboardArrowRightIcon fontSize="small" />
                                  </td>
                                  <td className="px-4 py-2 font-medium text-slate-600">{item.productTitle}</td>
                                  <td className="px-4 py-2 text-center text-slate-500">{item.unit}</td>
                                  <td className="px-4 py-2 text-right font-medium text-slate-600">{formatNumber(item.totalPoUsedWeight)}</td>
                                  <td className="px-4 py-2 text-right">
                                    <span className="font-bold text-slate-800">{formatNumber(item.totalNetWeight)}</span>
                                    <span className="text-gray-400 ml-1">
                                      / {item.unlimited ? "ไม่จำกัด" : formatNumber(item.totalPoWeight)}
                                    </span>
                                  </td>
                                  <td className="px-6 py-2 text-right">
                                    {renderOverWeight(overWeight, item.unlimited)}
                                  </td>
                                </tr>
                              );
                            })}
                          </React.Fragment>
                        );
                      });
                    })()}
                  </React.Fragment>
                );
              })}

              {/* No Sell Section Header */}
              {noSellGroups.length > 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-4 text-center font-bold text-slate-600 bg-gray-100 border-y border-gray-200 uppercase tracking-widest text-sm">
                    รายการสินค้าที่ลูกค้าไม่ได้นำมาขาย
                  </td>
                </tr>
              )}

              {noSellGroups.map(group => {
                const isExpanded = !!expandedGroups[group.poNumber];
                return (
                  <React.Fragment key={group.poNumber}>
                    <tr
                      className="border-b border-gray-100 bg-white hover:bg-gray-50/50 transition-colors cursor-pointer select-none"
                      onClick={() => toggleGroup(group.poNumber)}
                    >
                      <td className="px-4 py-3 text-center text-gray-400">
                        {isExpanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                      </td>
                      <td className="px-4 py-3 font-bold text-slate-700" colSpan={4}>{group.poNumber}</td>
                      <td className="px-4 py-3 text-right font-bold text-slate-700">0.00</td>
                      <td className="px-4 py-3 text-right font-bold text-slate-700">
                        <span className="text-slate-800">{formatNumber(group.totalNetWeight)}</span>
                        <span className="text-gray-400 font-normal"> / {group.isUnlimited ? "ไม่จำกัด" : formatNumber(group.totalPoWeight)}</span>
                      </td>
                      <td className="px-6 py-3 text-right">
                        {renderOverWeight(group.isUnlimited ? 0 : group.totalNetWeight - group.totalPoWeight, group.isUnlimited)}
                      </td>
                    </tr>
                    {/* Group Item Rows via SubCategory for No Sell */}
                    {isExpanded && (() => {
                      const subCatMap = {};
                      group.items.forEach(item => {
                        const maxC = item.subCategory || "อื่นๆ";
                        if (!subCatMap[maxC]) {
                          subCatMap[maxC] = {
                            name: maxC,
                            items: [],
                            totalNetWeight: 0,
                            totalPoWeight: 0,
                            unlimited: false
                          };
                        }
                        subCatMap[maxC].items.push(item);
                        /* Calculate totals by summing the original values behind poWeightText / overWeight */
                        const poWeightRaw = item.unlimited ? 0 : (item.totalNetWeight || 0 - (item.overWeight || 0)); // Hacky, let's just parse
                        subCatMap[maxC].totalNetWeight += item.currentWeight || 0; // It's actually totalNetWeight that was passed into the unused object.
                        // Actually, totalNetWeight is hidden in poWeightText. Let's fix the data generation!
                        // No need, we can just map the items without sub-summaries for Unused if it's too complex... Wait.
                        // Let's just group them without totals for Unsold since they are just a list.
                      });

                      return Object.values(subCatMap).map(subCat => {
                        const subCatKey = `${group.poNumber}-unused-${subCat.name}`;
                        const isSubExpanded = !!expandedGroups[subCatKey];

                        return (
                          <React.Fragment key={subCatKey}>
                            <tr
                              className="border-b border-gray-50 bg-gray-50/70 hover:bg-gray-100/70 transition-colors cursor-pointer select-none"
                              onClick={() => toggleGroup(subCatKey)}
                            >
                              <td className="px-4 py-2"></td>
                              <td className="px-4 py-2 text-center text-gray-400">
                                {isSubExpanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                              </td>
                              <td className="px-4 py-2 font-bold text-slate-700" colSpan={3}>
                                {subCat.name}
                              </td>
                              <td className="px-4 py-2 text-right font-bold text-slate-500">-</td>
                              <td className="px-4 py-2 text-right text-slate-500">-</td>
                              <td className="px-6 py-2 text-right">-</td>
                            </tr>

                            {/* Items inside SubCategory */}
                            {isSubExpanded && subCat.items.map((item) => {
                              return (
                                <tr key={item.id} className="border-b border-gray-50 bg-gray-50/30 hover:bg-gray-100/30">
                                  <td className="px-4 py-2"></td>
                                  <td className="px-4 py-2"></td>
                                  <td className="px-4 py-2 text-center text-gray-300">
                                    <KeyboardArrowRightIcon fontSize="small" />
                                  </td>
                                  <td className="px-4 py-2 font-medium text-slate-600">{item.productTitle}</td>
                                  <td className="px-4 py-2 text-center text-slate-500">{item.unit}</td>
                                  <td className="px-4 py-2 text-right font-medium text-slate-500">0.00</td>
                                  <td className="px-4 py-2 text-right text-slate-500">{item.poWeightText}</td>
                                  <td className="px-6 py-2 text-right">
                                    {renderOverWeight(item.overWeight, item.unlimited)}
                                  </td>
                                </tr>
                              );
                            })}
                          </React.Fragment>
                        );
                      });
                    })()}
                  </React.Fragment>
                );
              })}

              {soldGroups.length === 0 && noSellGroups.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-gray-400">
                    ไม่มีข้อมูลใบเสนอราคาในบิลนี้
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default POBreakdownGrid;
